import React, { PropsWithChildren, ReactElement, useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useModal } from 'store/modal';
import API from 'core/Api';
import { Column, CellProps } from 'react-table';
import Form from 'components/form/Form';
import FormGroup from 'components/form/FormGroup';
import ReactTableWrapper from 'components/table/ReactTableWrapper';
import { ISelectOptionProps, ISelectGroupProps } from 'components/form/types';
import { IResponseData } from 'types/api';
import { createErrorFromResponse } from 'core/Error';
import { usePermissionsContext } from 'store/permissions';
import { PermissionKeys } from 'store/types';
import { useTranslation } from 'react-i18next';
import { useLanguageContext } from 'store/language';
import Loader from 'components/Loader';
import setServerErrors from 'utils/helpers/formErrors';
import { getSubdomain } from 'utils/helpers/routing';
import { getUserInfo } from 'utils/auth';
import { useActiveClientContext } from 'store/active-client';
import { IScenario } from '../types';

interface IProps<T extends Record<string, unknown>> {
  data: IResponseData<T> | undefined;
}

function FormTable<T extends Record<string, unknown>>({ data }: PropsWithChildren<IProps<T>>): ReactElement {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const { showModal } = useModal();
  const { checkPermission } = usePermissionsContext();
  const { language } = useLanguageContext();
  const hasPermission = checkPermission(PermissionKeys.CAN_MANAGE_DOCUMENT_PERMISSIONS);
  const isOperator = getSubdomain() === 'operators';
  const userInfo = getUserInfo();
  const { client } = useActiveClientContext();

  const featureLeavesIsActive = isOperator ? client?.leaves : userInfo.featureLeavesIsActive;

  useEffect(() => {
    // To trigger columns rerender on lang change
    setIsLoading(true);
  }, [language]);

  useEffect(() => {
    if (isLoading) setIsLoading(false);
  }, [isLoading]);

  // Form submit
  const onSubmit = (submitData: any) => {
    const permissions = Object.keys(submitData).reduce((acc: any, curr) => {
      const slicedKey = curr.split('-');
      const catId = slicedKey[1];
      if (acc.find((a: IScenario) => a.category_id === catId)) return acc;
      acc.push({
        category_id: catId,
        create: buildActionOptions(submitData, catId, 'create'),
        approve: buildActionOptions(submitData, catId, 'approve'),
        sign: buildActionOptions(submitData, catId, 'sign'),
        send: buildActionOptions(submitData, catId, 'send'),
        notify: buildActionOptions(submitData, catId, 'notify'),
        leaves: buildActionOptions(submitData, catId, 'leaves'),
      });
      return acc;
    }, []);

    return API.patch('/category-permissions', {
      category_permissions: permissions,
    })
      .then(() => {
        showModal({
          type: 'success',
        });
      })
      .catch(res => handleErrorResponse(res, permissions));
  };

  // Default values for every single action
  const buildActVal = (val: ISelectGroupProps[]) => {
    const values = val?.map((i: ISelectGroupProps) => i.options?.filter((o: any) => o.selected));
    return values?.flat();
  };

  const lastInteractionRef = useRef({ name: '', selectedOptions: null });

  const applyChangesToAll = (name: any, selectedOptions: any) => {
    const [, , action] = name.split('-');
    data?.items.forEach((item: any) => {
      const fieldName = `category-${item.category.id}-${action}`;
      methods.setValue(fieldName as never, selectedOptions as never);
    });
    // Reset the last interaction after applying changes
    lastInteractionRef.current = { name: '', selectedOptions: null };
  };

  // Default values
  const buildDefaultValues = () => {
    const obj: any = {};
    data?.items.forEach((item: any) => {
      obj[`category-${item.category.id}-create`] = buildActVal(item.create);
      obj[`category-${item.category.id}-approve`] = buildActVal(item.approve);
      obj[`category-${item.category.id}-sign`] = buildActVal(item.sign);
      obj[`category-${item.category.id}-send`] = buildActVal(item.send);
      obj[`category-${item.category.id}-notify`] = buildActVal(item.notify);
      obj[`category-${item.category.id}-leaves`] = buildActVal(item.leaves);
    });
    return obj;
  };

  const methods = useForm<(fieldName: any, error: { type: any; message: any }) => void>({
    defaultValues: buildDefaultValues(),
  });

  if (isLoading)
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );

  // Render form control
  const renderFormGroup = (name: string, options: ISelectGroupProps[]) => {
    const handleChange = (selectedOptions: any) => {
      lastInteractionRef.current = { name, selectedOptions };
    };

    const handleMenuClose = () => {
      const lastInteraction = lastInteractionRef.current;

      if (lastInteraction.name && lastInteraction.name.split('-')[1] === '1') {
        showModal({
          type: 'confirm',
          heading: t('common.confirm'),
          content: t('structure.scenarios.confirmMassChange'),
          onConfirm: () => {
            applyChangesToAll(lastInteraction.name, lastInteraction.selectedOptions);
            // Reset the ref after applying changes
            lastInteractionRef.current = { name: '', selectedOptions: null };
          },
          onCancel: () => {
            // Reset the ref on cancel
            lastInteractionRef.current = { name: '', selectedOptions: null };
          },
        });
      }
    };

    return (
      <FormGroup
        controlId={`formGroup-${name}`}
        type="select"
        as="select"
        name={name}
        isClearable={hasPermission}
        isSearchable={hasPermission}
        isMulti
        showTotal
        allowSelectAll={hasPermission}
        selectOptions={options}
        disableOptions={!hasPermission}
        formGroupClassName="mb-0"
        onChange={handleChange}
        onMenuClose={handleMenuClose}
      />
    );
  };

  // Table columns
  const columns: Array<Column<any>> = [
    {
      Header: 'ID',
      accessor: 'id',
    },
    {
      Header: t('structure.scenarios.category') || '',
      accessor: 'category',
      width: 100,
      Cell: (cell: CellProps<IScenario>) => {
        return <>{cell.row.original.category.name}</>;
      },
    },
    {
      Header: t('structure.scenarios.createDocs') || '',
      accessor: 'create',
      disableSortBy: true,
      width: 180,
      Cell: (cell: CellProps<IScenario>) => {
        const name = `category-${cell.row.original.category.id}-create`;
        const options = cell.row.original.create;
        return <>{renderFormGroup(name, options)}</>;
      },
    },
    {
      Header: t('structure.scenarios.approveDocs') || '',
      accessor: 'approve',
      disableSortBy: true,
      width: 180,
      Cell: (cell: CellProps<IScenario>) => {
        const name = `category-${cell.row.original.category.id}-approve`;
        const options = cell.row.original.approve;
        return <>{renderFormGroup(name, options)}</>;
      },
    },
    {
      Header: t('structure.scenarios.signDocs') || '',
      accessor: 'sign',
      disableSortBy: true,
      width: 180,
      Cell: (cell: CellProps<IScenario>) => {
        const name = `category-${cell.row.original.category.id}-sign`;
        const options = cell.row.original.sign;
        return <>{renderFormGroup(name, options)}</>;
      },
    },
    {
      Header: t('structure.scenarios.sendAndArchiveDocs') || '',
      accessor: 'send',
      disableSortBy: true,
      width: 180,
      Cell: (cell: CellProps<IScenario>) => {
        const name = `category-${cell.row.original.category.id}-send`;
        const options = cell.row.original.send;
        return <>{renderFormGroup(name, options)}</>;
      },
    },
    {
      Header: t('structure.scenarios.notify') || '',
      accessor: 'notify',
      disableSortBy: true,
      width: 180,
      Cell: (cell: CellProps<IScenario>) => {
        const name = `category-${cell.row.original.category.id}-notify`;
        const options = cell.row.original.notify;
        return <>{renderFormGroup(name, options)}</>;
      },
    },
  ];

  // If Leaves module is active for the current user/tenant show this additional column for manage leaves
  if (featureLeavesIsActive) {
    columns.push({
      Header: t('structure.scenarios.manageLeaves') || '',
      accessor: 'leaves',
      disableSortBy: true,
      width: 180,
      Cell: (cell: CellProps<IScenario>) => {
        const name = `category-${cell.row.original.category.id}-leaves`;
        const options = cell.row.original.leaves;
        return <>{renderFormGroup(name, options)}</>;
      },
    });
  }

  // Build submit data for every single action in category
  const buildActionOptions = (submitData: any, catId: string, type: string) => {
    return submitData[`category-${catId}-${type}`]
      ? submitData[`category-${catId}-${type}`].map((d: ISelectOptionProps) => d.value)
      : [];
  };

  const handleErrorResponse = (res: any, permissions?: any) => {
    const response = createErrorFromResponse(res);
    const errors: any = {};
    Object.keys(response.errors).forEach(key => {
      // Process confirmation error in order to pass user confirmation when needed.
      if (key === 'confirmation_needed' && response.errors[key]) {
        showModal({
          type: 'confirm',
          heading: t('documents.table.confirmDocHeading'),
          content: t('structure.scenarios.confirmContent'),
          confirmLabel: t('documents.table.confirmRemoveDocLabel'),
          onConfirm: () => {
            return API.patch('/category-permissions', {
              // Here sending `confirmed: true` is required. Otherwise the request is not possible.
              confirmed: true,
              category_permissions: permissions,
            })
              .then(() => {
                showModal({
                  type: 'success',
                });
              })
              .catch(reqRes => handleErrorResponse(reqRes));
          },
        });
      }
      // Process fields errors in order to match field name with response error
      if (key === 'category_permissions') {
        const catPermErrGroup = response.errors[key];
        Object.keys(catPermErrGroup).forEach(catKey => {
          const actionGroup = catPermErrGroup[catKey];
          Object.keys(actionGroup).forEach(actionKey => {
            const optGroup = actionGroup[actionKey];
            const err: string[] = [];
            Object.keys(optGroup).forEach(optKey => {
              err.push(...optGroup[optKey]);
            });
            errors[`category-${Number(catKey) + 1}-${actionKey}`] = err;
          });
        });
      }
    });
    setServerErrors(errors, methods.setError);
  };

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      btnLabel={t('common.save')}
      isSubmitDisabled={!hasPermission}
      disabledBtnTooltip={t('common.noPermission')}
      hideCancelBtn
      showGlobalError
    >
      <ReactTableWrapper<T>
        data={data?.items || []}
        columns={columns}
        totalCount={data?.totalCount || 0}
        hideFilterBar
        skipUpdateColumns
        className="table-with-custom-dropdowns"
      />
    </Form>
  );
}

export default FormTable;

const LoaderWrapper = styled.div`
  min-height: 17rem;
  display: flex;
  justify-content: center;
  align-items: center;
`;
